import { differenceInWeeks } from 'date-fns';
import { darken } from 'polished';
import React from 'react';
import { useLocalStorage } from 'react-use';
import styled, { keyframes } from 'styled-components';
import { useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { Colors } from '../../../ui';
import { useResolve } from '../../Container';
import { AccountModel } from '../../account/AccountModel';
import { useOnTeamAddUserViaPopup } from '../../actions/WorkspaceActions';
import { PopupManager } from '../../popups/PopupManager';
import { WorkspaceModel } from '../Models';
import { Copy, Title } from '../elements/LayoutElements';

const Wrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-around;
    flex-wrap: wrap;

    @media (max-width: 146rem) {
        max-width: 60rem;
        margin: 0 auto;
        justify-content: center;
        gap: 3rem;
    }
`;

const Hero = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 9rem;
    height: 10rem;
    background: ${Colors.primary};
    border-radius: .75rem 0 0 .75rem;

    svg {
        width: 5rem;
        height: 5rem;
        color: white;
    }
`;

const Main = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    padding: 1rem 1.5rem;
    border-radius: 0 .75rem .75rem 0;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;

    ${Title} {
        margin: 0 0 .75em;
        font-weight: 500;
        font-size: .75rem;
        color: ${Colors.textLighter};
        text-transform: uppercase;
    }
    ${Copy} {
        margin: 0;
        line-height: 1.25em;
        font-size: 1.125rem;
        font-weight: 500;
        color: ${Colors.text};
    }
`;

const Action = styled.div`
    flex: 0 0 auto;
    border-radius: .75rem 0 0 .75rem;
    font-family: "Caveat";
    font-size: 2rem;
    font-weight: bold;
`;

const pulseAnim = (color: string) => keyframes`
    0% {
        opacity: 1;
        box-shadow: 0 0 0 0 ${color};
        border-radius: .75rem;
    }
    50%, 100% {
        opacity: 0;
        box-shadow: 0 0 0 2rem ${color};
        border-radius: 1rem;
    }
`;

const Pulser = styled.div`
    position: absolute;
    inset: 0;
`;

const Pulse = () => {
    const { current } = useObserver(useResolve(PopupManager));
    if (!!current) return null;
    return <Pulser />;
};

const Frame = styled.div<{ color: string }>`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    flex: 1 1 auto;
    max-width: 28rem;
    box-shadow: 0rem 0.25rem 1.5rem 0rem rgba(59, 101, 239, 0.10);
    border-radius: .75rem;
    user-select: none;
    background: #fff;

    ${Hero} {
        background: ${p => p.color};
    }
    ${Action} {
        color: ${p => p.color};
    }
    ${Pulser} {
        animation: ${p => pulseAnim(p.color)} 3s infinite;
        animation-delay: 3s;
    }

    &:hover {
        cursor: pointer;
        box-shadow: 
            0rem 0.25rem 1.5rem 0rem rgba(59, 101, 239, 0.15);

        ${Action} {
            color: ${p => p.color};
            text-decoration: underline;
        }
    }

    &:active {
        box-shadow: 
            0rem 0.25rem 1.5rem 0rem rgba(59, 101, 239, 0.15);

        background: #fcfcfc;
            
        ${Action} {
            color: ${p => darken(0.1, p.color)};
            text-decoration: underline;
        }
    }
`;

export const ProductTip = Object.assign(Frame, {
    Wrapper,
    Hero,
    Main,
    Body,
    Action,
    Pulse,
});

const WatchIcon = () =>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
        <path fill="currentColor" d="M 12 16 C 9.794 16 8 17.794 8 20 L 8 44 C 8 46.206 9.794 48 12 48 L 52 48 C 54.206 48 56 46.206 56 44 L 56 20 C 56 17.794 54.206 16 52 16 L 12 16 z M 13 20 L 51 20 C 51.552 20 52 20.448 52 21 L 52 43 C 52 43.552 51.552 44 51 44 L 13 44 C 12.448 44 12 43.552 12 43 L 12 21 C 12 20.448 12.448 20 13 20 z M 28.939453 26.251953 C 28.438969 26.282422 28 26.694703 28 27.251953 L 28 36.015625 C 28 36.759625 28.782266 37.242156 29.447266 36.910156 L 38.210938 32.527344 C 38.947938 32.158344 38.947938 31.107281 38.210938 30.738281 L 29.447266 26.357422 C 29.281016 26.274172 29.106281 26.241797 28.939453 26.251953 z M 22 51 C 20.896 51 20 51.896 20 53 C 20 54.104 20.896 55 22 55 L 42 55 C 43.104 55 44 54.104 44 53 C 44 51.896 43.104 51 42 51 L 22 51 z"></path>
    </svg>;

export const WatchProductTip = () => {
    const account = useResolve(AccountModel);
    const young = differenceInWeeks(new Date(), account.createdAt) < 4;
    const [watched, setWatched] = useLocalStorage('deqo/ui/tips/intro', false);
    const onWatch = (e: any) => {
        e.preventDefault();
        window.open('https://www.tella.tv/video/discover-metro-retro-3ckn', '_blank');
        setWatched(true);
    };
    return <ProductTip color={Colors.observe} onMouseUp={onWatch}>
        <ProductTip.Hero>
            <WatchIcon />
        </ProductTip.Hero>
        <ProductTip.Main>
            <ProductTip.Body>
                <Title>Watch</Title>
                <Copy>A 2 minute product tour with our co-founder Steve.</Copy>
            </ProductTip.Body>
            <ProductTip.Action>
                Watch Now
            </ProductTip.Action>
        </ProductTip.Main>
        {!watched && young && <ProductTip.Pulse />}
    </ProductTip>;
};


const LearnIcon = () =>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
        <path fill="currentColor" d="M 15 16 C 12.243 16 10 18.243 10 21 L 10 44 L 7 44 C 5.896 44 5 44.896 5 46 C 5 47.104 5.896 48 7 48 L 57 48 C 58.104 48 59 47.104 59 46 C 59 44.896 58.104 44 57 44 L 54 44 L 54 21 C 54 18.243 51.757 16 49 16 L 15 16 z M 15 20 L 49 20 C 49.552 20 50 20.449 50 21 L 50 44 L 14 44 L 14 21 C 14 20.449 14.448 20 15 20 z M 28.800781 27.371094 C 28.429531 27.387031 28.076344 27.542047 27.808594 27.810547 C 27.451594 28.168547 27.297391 28.682734 27.400391 29.177734 C 27.400391 29.177734 28.568359 34.773391 28.568359 34.775391 C 28.712359 35.464391 29.354594 35.980703 30.058594 35.970703 C 30.702594 35.962703 31.283656 35.525156 31.472656 34.910156 L 31.78125 33.902344 L 34.066406 36.1875 C 34.359406 36.4805 34.744906 36.626953 35.128906 36.626953 C 35.512906 36.626953 35.8955 36.4805 36.1875 36.1875 C 36.7735 35.6015 36.7735 34.651406 36.1875 34.066406 L 33.904297 31.783203 L 34.910156 31.474609 C 35.565156 31.273609 36.00075 30.654703 35.96875 29.970703 C 35.93775 29.286703 35.447344 28.712266 34.777344 28.572266 L 29.175781 27.402344 C 29.050531 27.375844 28.924531 27.365781 28.800781 27.371094 z"></path>
    </svg>;

export const LearnProductTip = () =>
    <ProductTip color={Colors.positive}>
        <ProductTip.Hero>
            <LearnIcon />
        </ProductTip.Hero>
        <ProductTip.Main>
            <ProductTip.Body>
                <Title>Learn</Title>
                <Copy>Try our Interactive Tutorial to get you going quickly!</Copy>
            </ProductTip.Body>
            <ProductTip.Action>
                Let&apos;s Go!
            </ProductTip.Action>
        </ProductTip.Main>
    </ProductTip>;

const HelpIcon = () =>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
        <path fill="currentColor" d="M 20 11 C 16.686 11 14 13.686 14 17 L 14 47 C 14 50.314 16.686 53 20 53 L 48 53 C 49.105 53 50 52.105 50 51 C 50 50.225188 49.556035 49.560676 48.912109 49.228516 L 48.917969 49.224609 C 48.917969 49.224609 47 48.445625 47 46.515625 C 47 44.621625 48.199219 43.845703 48.199219 43.845703 L 48.167969 43.837891 C 49.277417 42.920723 50 41.55158 50 40 L 50 17 C 50 13.686 47.314 11 44 11 L 20 11 z M 22 15 L 44 15 C 45.105 15 46 15.895 46 17 L 46 39 C 46 40.105 45.105 41 44 41 L 22 41 L 22 15 z M 27.5 20 C 26.672 20 26 20.671 26 21.5 C 26 22.329 26.672 23 27.5 23 L 40.5 23 C 41.328 23 42 22.329 42 21.5 C 42 20.671 41.328 20 40.5 20 L 27.5 20 z M 27.5 25 C 26.672 25 26 25.671 26 26.5 C 26 27.329 26.672 28 27.5 28 L 35.5 28 C 36.328 28 37 27.329 37 26.5 C 37 25.671 36.328 25 35.5 25 L 27.5 25 z M 20 45 L 43.363281 45 C 43.150753 45.537803 43 46.190793 43 47 C 43 47.805729 43.261883 48.458893 43.626953 49 L 20 49 C 18.895 49 18 48.105 18 47 C 18 45.895 18.895 45 20 45 z"></path>
    </svg>;

export const HelpProductTip = () =>
    <ProductTip color={Colors.mystery}>
        <ProductTip.Hero >
            <HelpIcon />
        </ProductTip.Hero>
        <ProductTip.Main>
            <ProductTip.Body>
                <Title>Help</Title>
                <Copy>Quick tips &amp; tricks to get you started with Ludi!</Copy>
            </ProductTip.Body>
            <ProductTip.Action>
                Open Docs
            </ProductTip.Action>
        </ProductTip.Main>
    </ProductTip>;

const CollaborateIcon = () =>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
        <path fill="currentColor" d="M 32 10 C 26.486 10 22 14.71 22 20.5 C 22 26.29 26.486 31 32 31 C 37.514 31 42 26.29 42 20.5 C 42 14.71 37.514 10 32 10 z M 32 14 C 35.309 14 38 16.916 38 20.5 C 38 24.084 35.309 27 32 27 C 28.691 27 26 24.084 26 20.5 C 26 16.916 28.691 14 32 14 z M 31.730469 34.142578 C 22.975469 34.142578 15.345141 38.854234 12.744141 45.865234 C 11.641141 48.839234 13.887688 52 17.054688 52 L 35.634766 52 C 35.226766 50.739 35 49.397 35 48 L 17.054688 48 C 16.547688 48 16.417141 47.465859 16.494141 47.255859 C 18.484141 41.889859 24.750469 38.140625 31.730469 38.140625 C 34.058469 38.140625 36.302844 38.565312 38.339844 39.320312 C 39.363844 38.181313 40.577359 37.221 41.943359 36.5 C 38.936359 34.994 35.437469 34.142578 31.730469 34.142578 z M 48 38 C 42.477 38 38 42.477 38 48 C 38 53.523 42.477 58 48 58 C 53.523 58 58 53.523 58 48 C 58 42.477 53.523 38 48 38 z M 48 42 C 48.553 42 49 42.448 49 43 L 49 47 L 53 47 C 53.553 47 54 47.448 54 48 C 54 48.552 53.553 49 53 49 L 49 49 L 49 53 C 49 53.552 48.553 54 48 54 C 47.447 54 47 53.552 47 53 L 47 49 L 43 49 C 42.447 49 42 48.552 42 48 C 42 47.448 42.447 47 43 47 L 47 47 L 47 43 C 47 42.448 47.447 42 48 42 z"></path>
    </svg>;

export const CollaborateProductTip = () => {

    const team = useResolve(WorkspaceModel);
    const { canTeamAddUser, onTeamAddUser } = useOnTeamAddUserViaPopup(team);
    if (!canTeamAddUser) return null;

    return <ProductTip color={Colors.primary} onClick={onTeamAddUser}>
        <ProductTip.Hero >
            <CollaborateIcon />
        </ProductTip.Hero>
        <ProductTip.Main>
            <ProductTip.Body>
                <Title>Collaborate</Title>
                <Copy>Invite team members and start collaborating.</Copy>
            </ProductTip.Body>
            <ProductTip.Action>
                Invite People
            </ProductTip.Action>
        </ProductTip.Main>
    </ProductTip>;
};

